import * as React from "react";
import lodash from "lodash";
import { FormikProps } from "formik";
import { RouteComponentProps } from "@talentpair/routing";
import { apiRequest, makeCancelable, CancelablePromiseT, ResponseDataT } from "@talentpair/api";
import { Loader, FlatButton } from "@talentpair/quantic";
import { ChoiceT } from "@talentpair/types/entities/misc";
import { PaginationT } from "@talentpair/types/misc";
import { PublicJobT } from "@talentpair/types/public";
import { JobBoardChoicesT, PublicJobChoicesT } from "@talentpair/types/entities/public";
import { objToQueryStr, queryStrToObj } from "@talentpair/utils";
import { CheckboxList } from "kyoto/components/Forms/Formik/fields/Checkbox";
import Form from "kyoto/components/Forms/Formik/Form";
import choiceService from "kyoto/services/choiceService";
import { toTruthy } from "kyoto/utils/array";
import {
  PublicOrganizations,
  PublicClassification,
  PublicLocations,
  PublicSeniority,
} from "./components/fields";
import BannerHeader from "../components/BannerHeader";
import Footer from "../components/Footer";
import SearchLayout from "./components/SearchLayout";
import { ResultsList } from "./components/ResultsList";
import { FilterValuesT } from "./components/types";
import { OrgBoardDetailT } from "../types";

const initialFilterValues = {
  organizations: [],
  classification: [],
  locations: [],
  seniority_scores: [],
  remotes: [],
};

interface QueryParamsT {
  orgs?: number[];
  lids?: number[];
  seniorities?: number[];
  fows?: number[];
  remotes?: number[];
}

const choicesToNumbers = (choices: ChoiceT[]): number[] =>
  choices.map(({ id }) => (id ? +id : null)).filter(toTruthy);

const mapFiltersToQueryParams = ({
  organizations,
  classification,
  locations,
  seniority_scores,
  remotes,
}: FilterValuesT): QueryParamsT => {
  const query: QueryParamsT = {};
  if (organizations.length) query.orgs = choicesToNumbers(organizations);
  if (classification.length) query.fows = choicesToNumbers(classification);
  if (seniority_scores.length) query.seniorities = choicesToNumbers(seniority_scores);
  if (locations.length) query.lids = choicesToNumbers(locations);
  if (remotes && remotes.length) query.remotes = remotes;
  return query;
};

const parseListParam = (val: string): number[] =>
  val === "" ? [] : val.split(",").map<number>((v) => +v);

const mapQueryParamsToFilters = (
  { orgs, lids, fows, seniorities, remotes }: Record<string, string | undefined>,
  choices: JobBoardChoicesT | PublicJobChoicesT,
): FilterValuesT => {
  const filters: FilterValuesT = { ...initialFilterValues };
  let params;
  if (orgs && "organizations" in choices) {
    params = parseListParam(orgs);
    filters.organizations = params
      .map((id) => choices.organizations.find((c) => c.id === id))
      .filter(toTruthy);
  }
  if (lids) {
    params = parseListParam(lids);
    filters.locations = params
      .map((id) => choices.locations.find((c) => c.id === id))
      .filter(toTruthy);
  }
  if (fows) {
    params = parseListParam(fows);
    filters.classification = params
      .map((id) => choices.classification.find((c) => c.id === id))
      .filter(toTruthy);
  }
  if (seniorities) {
    params = parseListParam(seniorities);
    filters.seniority_scores = params
      .map((id) => choices.seniority.find((c) => c.id === id))
      .filter(toTruthy);
  }

  if (remotes) {
    filters.remotes = parseListParam(remotes);
  }

  return filters;
};

const getFiltersFromQueryString = (query: string, choices: JobBoardChoicesT): FilterValuesT =>
  mapQueryParamsToFilters(
    queryStrToObj<"orgs" | "lids" | "fows" | "seniorities" | "remotes">(query),
    choices,
  );

const getQueryStringFromFilters = (values: FilterValuesT): string =>
  objToQueryStr(mapFiltersToQueryParams(values));

const jobLink = ({ id, slug }: PublicJobT): string => `/${id}/${slug}`;

export type QueryResponseT = ResponseDataT<PaginationT & { results: PublicJobT[] }>;

export interface JobBoardListProps extends RouteComponentProps<{ slug: string }> {
  formik: FormikProps<FilterValuesT>;
  org: OrgBoardDetailT;
  slug: string;
}

interface PublicJobsListState {
  pagination: null | PaginationT;
  results: PublicJobT[];
  loading: boolean;
}

class JobBoardList extends React.Component<JobBoardListProps, PublicJobsListState> {
  promise: null | CancelablePromiseT<QueryResponseT> = null;

  choices_cache: JobBoardChoicesT | null = null;

  state: PublicJobsListState = {
    results: [],
    pagination: null,
    loading: true,
  };

  componentDidMount(): Promise<unknown> {
    const choicesPromise = this.fetchAndCacheChoices();
    // if we're pulling filters from query params or the slug routes then we need to wait for choices to be fetched before we can do so
    // otherwise if we're just loading the initial unfiltered job results we can load them in parallel with the choices for a slightly faster experience
    // load filters from the slug (filters changing will submit the query)
    if (this.props.match.params.slug)
      return choicesPromise.then(() => this.getFiltersFromSlugRoute());
    // else load filters from the query params (filters changing will submit the query)

    if (this.props.location.search)
      return choicesPromise.then(() => this.getFiltersFromQueryParams());

    // else fetch results with our initial values (no filters)
    return Promise.all([choicesPromise, this.submitQuery()]);
  }

  componentDidUpdate({ location, formik: { values } }: JobBoardListProps): Promise<void> {
    // did the url query param change?
    // don't refetch because this method will change the form values which will trigger the query
    if (
      location.search !== this.props.location.search &&
      getQueryStringFromFilters(this.props.formik.values) !== this.props.location.search
    )
      return this.getFiltersFromQueryParams();

    // did the form filter values change?
    if (!lodash.isEqual(this.props.formik.values, values)) return this.submitQuery();

    return Promise.resolve();
  }

  componentWillUnmount(): void {
    if (this.promise) this.promise.cancel();
  }

  async fetchAndCacheChoices(): Promise<void> {
    this.choices_cache = await choiceService.job_board(this.props.slug);
  }

  getFiltersFromSlugRoute = (): Promise<void> => {
    const { history, match, formik } = this.props;
    if (!this.choices_cache) throw new Error("Missing filter choices.");
    if (!match.params.slug) return this.getFiltersFromQueryParams();
    const marketName = match.params.slug.split("-")[0];
    const locations = this.choices_cache.locations.filter(
      (m) => m.market?.toLowerCase().replace(" ", "") === marketName,
    );
    // remove market slug from url (regardless of whether we found the corresponding market choice)
    history.replace("/");
    if (locations.length) {
      const initialValues = { ...initialFilterValues, locations };
      formik.setValues(initialValues);
      return this.submitQuery(initialValues);
    }
    return this.getFiltersFromQueryParams();
  };

  getFiltersFromQueryParams = (): Promise<void> => {
    if (!this.choices_cache) throw new Error("Missing filter choices.");
    const initialValues = getFiltersFromQueryString(this.props.location.search, this.choices_cache);
    this.props.formik.setValues(initialValues);
    return Promise.resolve();
  };

  fetchResults = (url: string): Promise<void> => {
    if (!this.state.loading) this.setState({ loading: true });
    if (this.promise) this.promise.cancel();

    // memoizing this fetch request to prevent us from double-fetching
    // because this form must be bound to the location query params
    // we set the query params when the filters inputs change which will trigger a duplicate fetch request
    // memoization prevents this from happening
    this.promise = makeCancelable(apiRequest.getMemoized(url));

    return this.promise.then(({ data: { results, ...pagination } }: QueryResponseT) => {
      this.promise = null;
      this.setState((prevState) => ({
        results: lodash.uniqBy(prevState.results.concat(results), "id"),
        pagination,
        loading: false,
      }));
    });
  };

  submitQuery = (valuesOverride?: FilterValuesT): Promise<void> => {
    const {
      history,
      location,
      formik: { values },
      slug,
    } = this.props;
    const query = getQueryStringFromFilters(valuesOverride || values);
    // update url query params
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    history.replace(`${location.pathname}${query}`);
    // reset results list since filters have changed
    this.setState({ results: [], pagination: null, loading: true });

    return this.fetchResults(`jobs/board/${slug}/${query}`);
  };

  render(): React.ReactNode {
    const { results, pagination, loading } = this.state;
    const { formik, org, slug } = this.props;
    const showOrgsFilter = slug === "tp" || !!org.is_vc;
    const checkboxChoices = [
      { id: 1, name: "Remote available" },
      { id: 2, name: "100% remote" },
      { id: 3, name: "Hybrid remote" },
      { id: 0, name: "100% onsite" },
    ];

    return (
      <>
        <BannerHeader
          centerBanner
          title={
            <span className="regular">
              {org.job_board_header || "Check out these amazing opportunities"}
            </span>
          }
        />
        <SearchLayout
          filterTitle={<span className="h4 semibold">Filter jobs</span>}
          filters={
            <>
              <div className="px3 pb2 pt3 grey-border-bottom">
                {showOrgsFilter ? <PublicOrganizations /> : null}
                <PublicLocations />
                <CheckboxList
                  choices={checkboxChoices}
                  label="Remote"
                  FormControlLabelProps={{ classes: { root: "m0" } }}
                  FormGroupProps={{ row: false }}
                  name="remotes"
                />
                <PublicClassification />
                <PublicSeniority />
              </div>
              <div className="p2">
                <FlatButton color="primary" onClick={formik.handleReset}>
                  Clear filters
                </FlatButton>
              </div>
            </>
          }
          results={
            <>
              {pagination ? (
                <ResultsList
                  org={org}
                  slug={slug}
                  showOrganizations={showOrgsFilter}
                  results={results}
                  pagination={pagination}
                  fetchResults={this.fetchResults}
                  jobLink={jobLink}
                />
              ) : null}
              {loading ? <Loader /> : null}
            </>
          }
        />
        {pagination && !pagination.next ? <Footer org={org} /> : null}
      </>
    );
  }
}

interface SearchFormJobBoardProps extends JobBoardListProps {
  expose?: string;
}
export default function SearchForm({
  expose = undefined,
  ...props
}: SearchFormJobBoardProps): React.ReactElement {
  return (
    <Form<FilterValuesT>
      expose={expose}
      className="full-width flex-auto flex flex-column"
      initialValues={{ ...initialFilterValues }}
      render={(formik) => <JobBoardList {...props} formik={formik} />}
    />
  );
}

export const _test = {
  mapQueryParamsToFilters,
  mapFiltersToQueryParams,
  parseListParam,
  SearchForm,
};
